import type {
  ViewerProjectAssetCompleteResponse,
  ViewerProjectAssetUploadIntent,
  ViewerProjectAssetUploadIntentResponse,
  ViewerProjectDraft,
  ViewerProjectDraftResponse,
  ViewerJobStatus,
  ViewerJobStatusResponse,
  ViewerProjectStatus,
  ViewerProjectStatusResponse,
} from '../types/viewerDeployment';
import {
  readAdminApiJsonOrThrow,
  UnauthorizedViewerRequestError,
} from './adminApiClient';
import { buildApiUrl, buildPublicAppUrl } from './apiUrl.js';
import { authenticatedFetch } from '../lib/apiClient';
import { createBrowserSupabaseClient } from '../lib/supabaseClient';
import * as Sentry from '@sentry/react';
import { posthog } from '../lib/posthog';

type ViewerRouteTarget = 'preview' | 'live' | 'admin';
export type ViewerFileUploadScope = 'project' | 'workspace';

type ViewerFileUploadIntent = {
  fileId: string;
  provider: 'supabase_storage';
  bucket: string;
  objectKey: string;
  signedUploadUrl: string;
  uploadToken: string | null;
  expiresAt: string;
  requiresServerProcessing?: boolean;
};

type ViewerFileUploadIntentResponse = {
  success: true;
  data: ViewerFileUploadIntent;
};

type ViewerFileUploadCompleteResponse = {
  success: true;
  data: {
    fileId: string;
    readiness: 'staged' | 'processing' | 'ready' | 'failed';
    name: string;
  };
};

export type ViewerFileRecord = {
  file_id: string;
  workspace_id: string;
  project_id: string | null;
  name: string;
  asset_type: 'model' | 'image' | 'pdf' | 'video' | 'cad';
  readiness: 'staged' | 'processing' | 'ready' | 'failed';
  storage_provider: 'supabase_storage';
  bucket: string;
  object_key: string;
  size_bytes: number | null;
  mime_type: string | null;
  updated_at: string;
};

type ViewerFileListResponse = {
  success: true;
  data: ViewerFileRecord[];
};

type UploadViewerFileParams = {
  file: File;
  scope?: ViewerFileUploadScope;
};

type UploadAdminFileParams = {
  file: File;
  projectId?: string | null;
};

const FILE_UPLOAD_CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  '.dwg': 'application/acad',
  '.dxf': 'application/x-dxf',
  '.glb': 'model/gltf-binary',
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.mov': 'video/quicktime',
  '.mp4': 'video/mp4',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.step': 'application/step',
  '.stp': 'application/step',
  '.webm': 'video/webm',
  '.webp': 'image/webp',
};

function buildProjectAdminApiUrl(companyId: string, productId: string, action?: string): string {
  const basePath = `/api/admin/viewer-projects/${companyId}/${productId}`;
  return action ? `${basePath}?action=${encodeURIComponent(action)}` : basePath;
}

function buildViewerFileApiUrl(
  companyId: string,
  productId: string,
  options?: {
    action?: 'upload-intent' | 'complete' | 'content';
    fileId?: string;
    scope?: ViewerFileUploadScope;
  },
): string {
  const query = new URLSearchParams();
  if (options?.action) {
    query.set('action', options.action);
  }
  if (options?.fileId) {
    query.set('fileId', options.fileId);
  }
  if (options?.scope) {
    query.set('scope', options.scope);
  }

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return `/api/admin/viewer-projects/${companyId}/${productId}/files${suffix}`;
}

function buildAdminFilesApiUrl(options?: {
  action?: 'upload-intent' | 'complete' | 'content';
  fileId?: string;
  projectId?: string | null;
}): string {
  const query = new URLSearchParams();
  if (options?.action) {
    query.set('action', options.action);
  }
  if (options?.fileId) {
    query.set('fileId', options.fileId);
  }
  if (options?.projectId) {
    query.set('projectId', options.projectId);
  }

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return `/api/admin/files${suffix}`;
}

function resolveAssetUrl(assetUrl: string): string {
  if (/^https?:\/\//i.test(assetUrl)) {
    return assetUrl;
  }

  return buildApiUrl(assetUrl);
}

export { UnauthorizedViewerRequestError };

function getViewerFileContentType(file: File): string {
  const declaredType = file.type.trim();
  // Some browsers/platforms report a generic "application/octet-stream"
  // content-type for many files which causes strict server-side MIME
  // validation to reject otherwise-valid uploads. Prefer an explicit
  // extension-derived mapping when the declared type is missing or
  // the generic octet-stream value.
  if (declaredType.length > 0 && declaredType.toLowerCase() !== 'application/octet-stream') {
    return declaredType;
  }

  const lowerName = file.name.toLowerCase();
  const matchingEntry = Object.entries(FILE_UPLOAD_CONTENT_TYPE_BY_EXTENSION).find(
    ([extension]) => lowerName.endsWith(extension),
  );

  return matchingEntry?.[1] ?? 'application/octet-stream';
}

async function uploadViewerFileBinary(
  intent: ViewerFileUploadIntent,
  file: File,
  contentType: string,
): Promise<void> {
  const startTime = performance.now();

  if (intent.provider === 'supabase_storage') {
    const uploadToken = intent.uploadToken?.trim();
    if (!uploadToken) {
      const err = new Error('Supabase upload token is missing.');
      posthog.capture('supabase_upload_missing_token', {
        bucket: intent.bucket,
        object_key: intent.objectKey,
      });
      Sentry.captureException(err, {
        tags: { category: 'file.upload', subcategory: 'supabase_token_missing' },
        extra: { bucket: intent.bucket, objectKey: intent.objectKey },
      });
      throw err;
    }

    const supabase = createBrowserSupabaseClient();

    Sentry.addBreadcrumb({
      category: 'supabase.upload',
      message: 'Calling uploadToSignedUrl',
      data: {
        bucket: intent.bucket,
        objectKey: intent.objectKey,
        sizeBytes: file.size,
        contentType,
      },
    });

    const { error } = await supabase.storage
      .from(intent.bucket)
      .uploadToSignedUrl(intent.objectKey, uploadToken, file, {
        contentType,
      });

    const durationMs = performance.now() - startTime;

    if (error) {
      posthog.capture('supabase_upload_failed', {
        bucket: intent.bucket,
        object_key: intent.objectKey,
        size_bytes: file.size,
        content_type: contentType,
        duration_ms: Math.round(durationMs),
        error_message: error.message,
        error_name: (error as { name?: string }).name ?? 'Unknown',
        error_details: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      });
      Sentry.captureException(error, {
        tags: { category: 'file.upload', subcategory: 'supabase_binary' },
        extra: {
          bucket: intent.bucket,
          objectKey: intent.objectKey,
          sizeBytes: file.size,
          durationMs,
        },
      });
      throw new Error(`File upload failed: ${error.message}`);
    }

    posthog.capture('supabase_upload_success', {
      bucket: intent.bucket,
      object_key: intent.objectKey,
      size_bytes: file.size,
      duration_ms: Math.round(durationMs),
    });
    return;
  }

  const uploadResponse = await fetch(intent.signedUploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  });

  const durationMs = performance.now() - startTime;

  if (!uploadResponse.ok) {
    const responseText = await uploadResponse.text().catch(() => '');
    posthog.capture('s3_upload_failed', {
      status: uploadResponse.status,
      size_bytes: file.size,
      duration_ms: Math.round(durationMs),
      response_text: responseText.slice(0, 500),
    });
    Sentry.captureException(new Error(`S3 upload failed: ${uploadResponse.status}`), {
      tags: { category: 'file.upload', subcategory: 's3_binary' },
      extra: {
        status: uploadResponse.status,
        sizeBytes: file.size,
        responseText: responseText.slice(0, 500),
      },
    });
    throw new Error(`File upload failed with status ${uploadResponse.status}`);
  }
}

export function buildViewerRouteUrl(
  kind: ViewerRouteTarget,
  companyId: string,
  productId: string,
  explicitBaseUrl?: string,
): string {
  const viewerBaseUrl = explicitBaseUrl ?? import.meta.env.VITE_VIEWER_BASE_URL?.trim();
  return buildPublicAppUrl(`/viewer/${kind}/${companyId}/${productId}`, viewerBaseUrl);
}

export async function fetchViewerProjectStatus(companyId: string, productId: string): Promise<ViewerProjectStatus> {
  const response = await authenticatedFetch(buildApiUrl(buildProjectAdminApiUrl(companyId, productId)), {
    method: 'GET',
  });

  const payload = await readAdminApiJsonOrThrow<ViewerProjectStatusResponse>(response);
  return payload.data as ViewerProjectStatus;
}

export async function fetchViewerProjectDraft(companyId: string, productId: string): Promise<ViewerProjectDraft | null> {
  const response = await authenticatedFetch(buildApiUrl(buildProjectAdminApiUrl(companyId, productId, 'draft')), {
    method: 'GET',
  });

  const payload = await readAdminApiJsonOrThrow<ViewerProjectDraftResponse>(response);
  return (payload.data as ViewerProjectDraft | null | undefined) ?? null;
}

export async function saveViewerProjectDraft(
  companyId: string,
  productId: string,
  body: { expectedRevision: number } & Record<string, unknown>,
): Promise<ViewerProjectDraft> {
  const response = await authenticatedFetch(buildApiUrl(buildProjectAdminApiUrl(companyId, productId, 'draft')), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const payload = await readAdminApiJsonOrThrow<ViewerProjectDraftResponse>(response);
  return payload.data as ViewerProjectDraft;
}

export async function requestProjectImageUploadIntent(
  companyId: string,
  productId: string,
  body: {
    entityType: 'tool' | 'part' | 'project';
    entityId: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
  },
): Promise<ViewerProjectAssetUploadIntent> {
  const response = await authenticatedFetch(buildApiUrl(`/api/admin/viewer-projects/${companyId}/${productId}/assets?action=upload-intent`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const payload = await readAdminApiJsonOrThrow<ViewerProjectAssetUploadIntentResponse>(response);
  return payload.data as ViewerProjectAssetUploadIntent;
}

export async function completeProjectAssetUpload(
  companyId: string,
  productId: string,
  assetId: string,
): Promise<{ assetId: string; assetUrl: string; status: 'ready' }> {
  const response = await authenticatedFetch(
    buildApiUrl(`/api/admin/viewer-projects/${companyId}/${productId}/assets?action=complete&assetId=${encodeURIComponent(assetId)}`),
    {
      method: 'POST',
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerProjectAssetCompleteResponse>(response);
  return payload.data as { assetId: string; assetUrl: string; status: 'ready' };
}

export async function uploadProjectImageAsset(
  companyId: string,
  productId: string,
  entityType: 'tool' | 'part' | 'project',
  entityId: string,
  file: File,
): Promise<string> {
  const intent = await requestProjectImageUploadIntent(companyId, productId, {
    entityType,
    entityId,
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });

  const uploadResponse = await fetch(intent.uploadUrl, {
    method: intent.method,
    headers: intent.headers,
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Asset upload failed.');
  }

  const completed = await completeProjectAssetUpload(companyId, productId, intent.assetId);
  return resolveAssetUrl(completed.assetUrl);
}

export async function requestViewerFileUploadIntent(
  companyId: string,
  productId: string,
  body: {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    scope?: ViewerFileUploadScope;
  },
): Promise<ViewerFileUploadIntent> {
  const response = await authenticatedFetch(
    buildApiUrl(
      buildViewerFileApiUrl(companyId, productId, {
        action: 'upload-intent',
        scope: body.scope,
      }),
    ),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName: body.fileName,
        mimeType: body.mimeType,
        sizeBytes: body.sizeBytes,
      }),
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerFileUploadIntentResponse>(response);
  return payload.data;
}

export async function completeViewerFileUpload(
  companyId: string,
  productId: string,
  fileId: string,
  scope: ViewerFileUploadScope = 'project',
): Promise<ViewerFileUploadCompleteResponse['data']> {
  const response = await authenticatedFetch(
    buildApiUrl(
      buildViewerFileApiUrl(companyId, productId, {
        action: 'complete',
        fileId,
        scope,
      }),
    ),
    {
      method: 'POST',
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerFileUploadCompleteResponse>(response);
  return payload.data;
}

export function getViewerFileContentUrl(
  companyId: string,
  productId: string,
  fileId: string,
  scope: ViewerFileUploadScope = 'project',
): string {
  return buildApiUrl(
    buildViewerFileApiUrl(companyId, productId, {
      action: 'content',
      fileId,
      scope,
    }),
  );
}

export async function listViewerFiles(
  companyId: string,
  productId: string,
  scope: ViewerFileUploadScope = 'project',
): Promise<ViewerFileRecord[]> {
  const response = await authenticatedFetch(
    buildApiUrl(
      buildViewerFileApiUrl(companyId, productId, {
        scope,
      }),
    ),
    {
      method: 'GET',
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerFileListResponse>(response);
  return payload.data;
}

export async function uploadViewerFile(
  companyId: string,
  productId: string,
  params: UploadViewerFileParams,
): Promise<{
  fileId: string;
  contentUrl: string;
  provider: ViewerFileUploadIntent['provider'];
  readiness: ViewerFileUploadCompleteResponse['data']['readiness'];
}> {
  const scope = params.scope ?? 'project';
  const contentType = getViewerFileContentType(params.file);
  const intent = await requestViewerFileUploadIntent(companyId, productId, {
    fileName: params.file.name,
    mimeType: contentType,
    sizeBytes: params.file.size,
    scope,
  });

  await uploadViewerFileBinary(intent, params.file, contentType);
  const completed = await completeViewerFileUpload(
    companyId,
    productId,
    intent.fileId,
    scope,
  );

  return {
    fileId: completed.fileId,
    contentUrl: getViewerFileContentUrl(companyId, productId, completed.fileId, scope),
    provider: intent.provider,
    readiness: completed.readiness,
  };
}

export async function requestAdminFileUploadIntent(body: {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  projectId?: string | null;
}): Promise<ViewerFileUploadIntent> {
  const response = await authenticatedFetch(
    buildApiUrl(
      buildAdminFilesApiUrl({
        action: 'upload-intent',
        projectId: body.projectId,
      }),
    ),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName: body.fileName,
        mimeType: body.mimeType,
        sizeBytes: body.sizeBytes,
      }),
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerFileUploadIntentResponse>(response);
  return payload.data;
}

export async function completeAdminFileUpload(
  fileId: string,
  projectId?: string | null,
): Promise<ViewerFileUploadCompleteResponse['data']> {
  const response = await authenticatedFetch(
    buildApiUrl(
      buildAdminFilesApiUrl({
        action: 'complete',
        fileId,
        projectId,
      }),
    ),
    {
      method: 'POST',
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerFileUploadCompleteResponse>(response);
  return payload.data;
}

export function getAdminFileContentUrl(fileId: string, projectId?: string | null): string {
  return buildApiUrl(
    buildAdminFilesApiUrl({
      action: 'content',
      fileId,
      projectId,
    }),
  );
}

export async function listAdminFiles(projectId?: string | null): Promise<ViewerFileRecord[]> {
  const response = await authenticatedFetch(
    buildApiUrl(
      buildAdminFilesApiUrl({
        projectId,
      }),
    ),
    {
      method: 'GET',
    },
  );

  const payload = await readAdminApiJsonOrThrow<ViewerFileListResponse>(response);
  return payload.data;
}

export async function uploadAdminFile(
  params: UploadAdminFileParams,
): Promise<{
  fileId: string;
  contentUrl: string;
  provider: ViewerFileUploadIntent['provider'];
  readiness: ViewerFileUploadCompleteResponse['data']['readiness'];
}> {
  Sentry.addBreadcrumb({
    category: 'file.upload',
    message: 'uploadAdminFile started',
    data: {
      fileName: params.file.name,
      sizeBytes: params.file.size,
      mimeType: params.file.type,
      projectId: params.projectId ?? null,
    },
  });

  const contentType = getViewerFileContentType(params.file);

  try {
    let intent: ViewerFileUploadIntent;
    try {
      intent = await requestAdminFileUploadIntent({
        fileName: params.file.name,
        mimeType: contentType,
        sizeBytes: params.file.size,
        projectId: params.projectId,
      });
      Sentry.addBreadcrumb({
        category: 'file.upload',
        message: 'Upload intent received',
        data: {
          fileId: intent.fileId,
          bucket: intent.bucket,
          objectKey: intent.objectKey,
          provider: intent.provider,
          expiresAt: intent.expiresAt,
        },
      });
    } catch (err) {
      posthog.capture('file_upload_intent_failed', {
        file_name: params.file.name,
        size_bytes: params.file.size,
        error: err instanceof Error ? err.message : String(err),
      });
      Sentry.captureException(err, {
        tags: { category: 'file.upload', stage: 'intent' },
        extra: { fileName: params.file.name },
      });
      throw err;
    }

    try {
      if (intent.requiresServerProcessing) {
        // GLB files are uploaded to the backend for Draco compression before
        // being stored in Supabase Storage. This avoids the 50MB per-file limit
        // by compressing the file server-side first.
        const uploadGlbUrl = buildApiUrl(
          `/api/admin/files/upload-glb?fileId=${encodeURIComponent(intent.fileId)}` +
          (params.projectId ? `&projectId=${encodeURIComponent(params.projectId)}` : ''),
        );
        const response = await authenticatedFetch(uploadGlbUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: params.file,
        });
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`Server-side GLB compression failed: ${errorText}`);
        }
      } else {
        await uploadViewerFileBinary(intent, params.file, contentType);
      }
      Sentry.addBreadcrumb({
        category: 'file.upload',
        message: 'Binary upload completed',
        data: { fileId: intent.fileId, serverProcessed: !!intent.requiresServerProcessing },
      });
    } catch (err) {
      posthog.capture('file_upload_binary_failed', {
        file_name: params.file.name,
        size_bytes: params.file.size,
        bucket: intent.bucket,
        provider: intent.provider,
        error: err instanceof Error ? err.message : String(err),
      });
      Sentry.captureException(err, {
        tags: { category: 'file.upload', stage: 'binary' },
        extra: { fileName: params.file.name, bucket: intent.bucket },
      });
      throw err;
    }

    let completed: ViewerFileUploadCompleteResponse['data'];
    try {
      completed = await completeAdminFileUpload(intent.fileId, params.projectId);
      Sentry.addBreadcrumb({
        category: 'file.upload',
        message: 'Upload completion confirmed',
        data: { fileId: completed.fileId, readiness: completed.readiness },
      });
    } catch (err) {
      posthog.capture('file_upload_complete_failed', {
        file_name: params.file.name,
        file_id: intent.fileId,
        error: err instanceof Error ? err.message : String(err),
      });
      Sentry.captureException(err, {
        tags: { category: 'file.upload', stage: 'complete' },
        extra: { fileName: params.file.name, fileId: intent.fileId },
      });
      throw err;
    }

    posthog.capture('file_upload_success', {
      file_name: params.file.name,
      size_bytes: params.file.size,
      file_id: completed.fileId,
      project_id: params.projectId ?? null,
      provider: intent.provider,
    });

    return {
      fileId: completed.fileId,
      contentUrl: getAdminFileContentUrl(completed.fileId, params.projectId),
      provider: intent.provider,
      readiness: completed.readiness,
    };
  } catch (error) {
    posthog.capture('file_upload_failed', {
      file_name: params.file.name,
      size_bytes: params.file.size,
      mime_type: params.file.type,
      project_id: params.projectId ?? null,
      error_message: error instanceof Error ? error.message : String(error),
      error_stage: 'unknown',
    });
    Sentry.captureException(error, {
      tags: { category: 'file.upload', stage: 'unknown' },
      extra: {
        fileName: params.file.name,
        sizeBytes: params.file.size,
        mimeType: params.file.type,
      },
    });
    throw error;
  }
}

export async function createPreviewRelease(
  companyId: string,
  productId: string,
  idempotencyKey: string,
  expectedDraftRevision?: number,
): Promise<Record<string, unknown>> {
  const response = await authenticatedFetch(buildApiUrl(buildProjectAdminApiUrl(companyId, productId, 'preview')), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify({ idempotencyKey, expectedDraftRevision }),
  });

  const payload = await readAdminApiJsonOrThrow<{ success: boolean; data?: Record<string, unknown>; error?: string }>(response);
  return payload.data ?? {};
}

export async function deployViewerProject(
  companyId: string,
  productId: string,
  idempotencyKey: string,
  sourceReleaseId?: string,
): Promise<Record<string, unknown>> {
  const response = await authenticatedFetch(buildApiUrl(buildProjectAdminApiUrl(companyId, productId, 'deploy')), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify({ idempotencyKey, sourceReleaseId }),
  });

  const payload = await readAdminApiJsonOrThrow<{ success: boolean; data?: Record<string, unknown>; error?: string }>(response);
  return payload.data ?? {};
}

export async function fetchDeploymentJobStatus(
  companyId: string,
  productId: string,
  jobId: string,
): Promise<ViewerJobStatus> {
  const response = await authenticatedFetch(buildApiUrl(`/api/admin/viewer-projects/${companyId}/${productId}/jobs?jobId=${encodeURIComponent(jobId)}`), {
    method: 'GET',
  });

  const payload = await readAdminApiJsonOrThrow<ViewerJobStatusResponse>(response);
  return payload.data as ViewerJobStatus;
}


// ---------------------------------------------------------------------------
// Model Assignment — assigns an uploaded file as the active model for a
// viewer project. This creates the explicit link between the files table
// and the viewer project draft.
// ---------------------------------------------------------------------------

export type AssignModelResult = {
  modelFileId: string;
  draftRevision: number;
};

export async function assignModelToViewerProject(
  companyId: string,
  productId: string,
  fileId: string,
): Promise<AssignModelResult> {
  console.info('[viewer-model-assign] assigning model to project', {
    companyId,
    productId,
    fileId,
  });

  const response = await authenticatedFetch(
    buildApiUrl(buildProjectAdminApiUrl(companyId, productId, 'set-model')),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileId }),
    },
  );

  const payload = await readAdminApiJsonOrThrow<{
    success: boolean;
    data: AssignModelResult;
  }>(response);

  console.info('[viewer-model-assign] model assigned successfully', {
    companyId,
    productId,
    fileId,
    draftRevision: payload.data.draftRevision,
  });

  return payload.data;
}
